/**
 * ExportImportModal 组件
 *
 * 会话导出/导入模态框，提供：
 * - 导出单个/选中的会话为JSONL
 * - 导出所有会话
 * - 导入JSONL文件
 * - 导入预览
 */

import React, { useState, useCallback, useRef, ChangeEvent } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useChatStore } from '../../store/chatStore';
import { chatSessionApi } from '../../services/chatSessionApi';

// 图标组件
const DownloadIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const UploadIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FileIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

interface ExportImportModalProps {
  /** 工作区ID */
  workspaceId: number;
  /** JWT认证令牌 */
  token: string;
  /** 是否显示模态框 */
  isOpen: boolean;
  /** 关闭模态框 */
  onClose: () => void;
  /** 选中的会话ID列表（用于导出） */
  selectedSessionIds?: string[];
  /** 成功回调 */
  onSuccess?: () => void;
}

type TabType = 'export' | 'import';
type ExportType = 'selected' | 'all';
type ConflictResolution = 'skip' | 'overwrite' | 'rename';

interface ImportPreview {
  total_sessions: number;
  total_messages: number;
  sessions: Array<{
    id: string;
    title: string;
    model: string;
    message_count: number;
  }>;
}

interface ImportResult {
  imported_count: number;
  skipped_count: number;
  errors: string[];
}

export const ExportImportModal: React.FC<ExportImportModalProps> = ({
  workspaceId,
  token,
  isOpen,
  onClose,
  selectedSessionIds = [],
  onSuccess,
}) => {
  const { fetchSessions } = useChatStore();

  // Tab状态
  const [activeTab, setActiveTab] = useState<TabType>('export');

  // 导出状态
  const [exportType, setExportType] = useState<ExportType>('selected');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // 导入状态
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [conflictResolution, setConflictResolution] = useState<ConflictResolution>('skip');

  // 文件输入ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 重置状态
  const resetState = useCallback(() => {
    setExportError(null);
    setImportFile(null);
    setPreview(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // 切换Tab时重置状态
  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
    resetState();
  }, [resetState]);

  // 处理导出
  const handleExport = useCallback(async () => {
    if (exportType === 'selected' && selectedSessionIds.length === 0) {
      setExportError('请先选择要导出的会话');
      return;
    }

    setExporting(true);
    setExportError(null);

    try {
      const data = exportType === 'all'
        ? await chatSessionApi.exportAllSessions(workspaceId)
        : await chatSessionApi.exportSessions(selectedSessionIds, workspaceId);

      const blob = new Blob([data.content], { type: 'application/jsonl' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      setExportError(error instanceof Error ? error.message : '导出失败，请重试');
    } finally {
      setExporting(false);
    }
  }, [exportType, selectedSessionIds, workspaceId, onClose, onSuccess]);

  // 处理文件选择
  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.name.endsWith('.jsonl')) {
      alert('请选择.jsonl格式的文件');
      return;
    }

    // 验证文件大小（50MB）
    if (file.size > 50 * 1024 * 1024) {
      alert('文件大小不能超过50MB');
      return;
    }

    setImportFile(file);
    setPreview(null);
    setImportResult(null);
  }, []);

  // 读取文件内容
  const readFileContent = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('文件读取失败'));
      reader.readAsText(file);
    });
  }, []);

  // 预览导入
  const handlePreviewImport = useCallback(async () => {
    if (!importFile) return;

    setImporting(true);

    try {
      const content = await readFileContent(importFile);
      const previewData = await chatSessionApi.previewImport(content, workspaceId);

      setPreview({
        total_sessions: previewData.total,
        total_messages: previewData.sessions.reduce((sum, s) => sum + s.message_count, 0),
        sessions: previewData.sessions.map(s => ({
          id: '',
          title: s.title,
          model: '',
          message_count: s.message_count,
        })),
      });
    } catch (error) {
      alert(error instanceof Error ? error.message : '预览失败，请重试');
    } finally {
      setImporting(false);
    }
  }, [importFile, workspaceId, readFileContent]);

  // 执行导入
  const handleImport = useCallback(async () => {
    if (!importFile) return;

    setImporting(true);

    try {
      const content = await readFileContent(importFile);
      const result = await chatSessionApi.importSessions(content, workspaceId, conflictResolution);

      setImportResult({
        imported_count: result.imported_count,
        skipped_count: result.skipped_count,
        errors: result.errors || [],
      });

      await fetchSessions(token, workspaceId, 1, 20);

      if (result.imported_count > 0) {
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : '导入失败，请重试');
    } finally {
      setImporting(false);
    }
  }, [importFile, token, workspaceId, conflictResolution, readFileContent, fetchSessions, onSuccess]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="会话导出/导入"
      size="lg"
      closeOnOverlayClick={!exporting && !importing}
    >
      {/* Tab切换 */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => handleTabChange('export')}
          className={`flex-1 px-4 py-3 font-medium transition-colors ${
            activeTab === 'export'
              ? 'text-gray-700 border-b-2 border-gray-600 bg-white'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <DownloadIcon />
            <span>导出会话</span>
          </div>
        </button>
        <button
          onClick={() => handleTabChange('import')}
          className={`flex-1 px-4 py-3 font-medium transition-colors ${
            activeTab === 'import'
              ? 'text-gray-700 border-b-2 border-gray-600 bg-white'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <UploadIcon />
            <span>导入会话</span>
          </div>
        </button>
      </div>

      {/* 导出Tab */}
      {activeTab === 'export' && (
        <div className="space-y-6">
          {/* 导出选项 */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">选择导出范围</label>

            <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
              exportType === 'selected' ? 'border-gray-500 bg-white' : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="radio"
                name="exportType"
                checked={exportType === 'selected'}
                onChange={() => setExportType('selected')}
                className="mt-1"
                disabled={exporting}
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">导出选中的会话</div>
                <div className="text-sm text-gray-700 mt-1">
                  已选择 {selectedSessionIds.length} 个会话
                  {selectedSessionIds.length === 0 && '（请先在会话列表中选择要导出的会话）'}
                </div>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
              exportType === 'all' ? 'border-gray-500 bg-white' : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="radio"
                name="exportType"
                checked={exportType === 'all'}
                onChange={() => setExportType('all')}
                className="mt-1"
                disabled={exporting}
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">导出所有会话</div>
                <div className="text-sm text-gray-700 mt-1">
                  导出当前工作区的所有会话
                </div>
              </div>
            </label>
          </div>

          {/* 错误信息 */}
          {exportError && (
            <div className="p-3 bg-red-50 border border-red-300 rounded-lg text-sm text-red-700">
              {exportError}
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={exporting}
            >
              取消
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={exporting || (exportType === 'selected' && selectedSessionIds.length === 0)}
              loading={exporting}
              leftIcon={<DownloadIcon />}
            >
              {exporting ? '导出中...' : '导出'}
            </Button>
          </div>
        </div>
      )}

      {/* 导入Tab */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* 文件上传 */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">选择JSONL文件</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jsonl"
              onChange={handleFileChange}
              disabled={importing}
              className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-800 hover:file:bg-gray-200 disabled:opacity-50"
            />
            <p className="text-xs text-gray-600 mt-1">
              支持最大50MB的JSONL格式文件
            </p>
          </div>

          {/* 文件信息和预览按钮 */}
          {importFile && !preview && (
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <FileIcon />
                <div>
                  <div className="font-medium text-gray-900">{importFile.name}</div>
                  <div className="text-sm text-gray-700">
                    {(importFile.size / 1024).toFixed(2)} KB
                  </div>
                </div>
              </div>
              <Button
                variant="secondary"
                onClick={handlePreviewImport}
                disabled={importing}
                loading={importing}
              >
                预览
              </Button>
            </div>
          )}

          {/* 预览结果 */}
          {preview && (
            <div className="space-y-4">
              <div className="p-4 bg-white border border-gray-200 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">导入预览</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-700">会话数量：</span>
                    <span className="font-medium">{preview.total_sessions}</span>
                  </div>
                  <div>
                    <span className="text-gray-700">消息数量：</span>
                    <span className="font-medium">{preview.total_messages}</span>
                  </div>
                </div>
              </div>

              {/* 会话列表 */}
              {preview.sessions.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-white sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-900">会话标题</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-900">模型</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-900">消息数</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {preview.sessions.map((session, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-gray-900">{session.title}</td>
                          <td className="px-4 py-2 text-gray-700">{session.model}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{session.message_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 冲突解决策略 */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">如果会话已存在</label>
                <select
                  value={conflictResolution}
                  onChange={(e) => setConflictResolution(e.target.value as ConflictResolution)}
                  disabled={importing}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  <option value="skip">跳过（默认）</option>
                  <option value="rename">重命名导入</option>
                  <option value="overwrite">覆盖</option>
                </select>
              </div>
            </div>
          )}

          {/* 导入结果 */}
          {importResult && (
            <div className={`p-4 border rounded-lg ${
              importResult.imported_count > 0
                ? 'bg-green-50 border-green-300'
                : 'bg-yellow-50 border-yellow-300'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <CheckIcon />
                <span className="font-medium text-gray-900">导入完成</span>
              </div>
              <div className="text-sm space-y-1">
                <div>成功导入 {importResult.imported_count} 个会话</div>
                {importResult.skipped_count > 0 && (
                  <div>跳过 {importResult.skipped_count} 个会话</div>
                )}
                {importResult.errors.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-red-700">查看错误 ({importResult.errors.length})</summary>
                    <ul className="mt-2 space-y-1 pl-4">
                      {importResult.errors.map((error, index) => (
                        <li key={index} className="text-red-600">{error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={importing}
            >
              {importResult ? '关闭' : '取消'}
            </Button>
            {preview && !importResult && (
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={importing}
                loading={importing}
                leftIcon={<UploadIcon />}
              >
                {importing ? '导入中...' : '开始导入'}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ExportImportModal;
